import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { PageEngagementRow } from '@/api/types';
import { Callout } from './Callout';
import { formatCompact } from './TrendChart';

interface TimeOnPageProps {
  siteKey: string;
  from: string;
  to: string;
}

/**
 * How long visitors spend on each page.
 *
 * <b>Two columns, never one.</b> GA4's `userEngagementDuration` measures engaged-user time and is
 * suppressed by ad blockers; the first-party tracker measures visible-tab time and is not blocked.
 * They will disagree, and the size of the disagreement is the useful part — it is the closest thing
 * to a measure of how much traffic GA4 never sees. Averaging them into one "time on page" would
 * destroy exactly that, so the two stay side by side with their sources named.
 *
 * A dash means <b>not measured</b>. It never means zero — a page with no timing data and a page
 * nobody read for a single second are different facts, and 0s would assert the second one.
 */
export function TimeOnPage({ siteKey, from, to }: TimeOnPageProps) {
  const query = useQuery({
    queryKey: ['page-engagement', siteKey, from, to],
    queryFn: () => api.pageEngagement(siteKey, from, to),
    retry: false,
    placeholderData: (previous) => previous,
  });

  const data = query.data;
  const rows = data?.rows ?? [];

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Time on page</h2>
        <span className="t-label subtle">
          {data?.has_rum
            ? 'GA4 and first-party, side by side'
            : 'GA4 · first-party appears once the tracker is live'}
        </span>
      </div>

      <div className={`card-body${query.isFetching ? ' is-refetching' : ''}`}>
        {rows.length === 0 ? (
          <div className="empty-state t-body">
            No page-timing data in this range yet.
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Page</th>
                    <th className="num">GA4 avg</th>
                    <th className="num">GA4 views</th>
                    <th className="num">First-party avg</th>
                    <th className="num">Views</th>
                    <th className="num">Ratio</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.page}>
                      <td>
                        <code>{row.page}</code>
                        {aliasNote(row) && (
                          <span className="t-caption subtle" style={{ marginInlineStart: 8 }}>
                            {/* The alias split, so a merge is never silent — half the traffic
                                landing on /home instead of / is only visible here. */}
                            incl. {aliasNote(row)}
                          </span>
                        )}
                      </td>
                      <td className="num">{duration(row.ga4_avg_seconds)}</td>
                      <td className="num">{count(row.ga4_views)}</td>
                      <td className="num">{duration(row.rum_avg_seconds)}</td>
                      <td className="num">{count(row.rum_views)}</td>
                      <td className="num">{ratio(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {data?.untracked_rum_seconds != null && (
              <p className="t-caption subtle" style={{ marginBottom: 0 }}>
                {duration(data.untracked_rum_seconds)} of first-party dwell fell outside the tracked
                page list and is counted in the totals but not in any row above.
              </p>
            )}

            {data?.has_ga4 && data.has_rum && (
              <Callout tone="info" title="Why the two columns differ">
                GA4 measures engaged-user time and is blocked by ad blockers; the first-party
                tracker measures visible-tab time and is not. A ratio above 1 is an estimate of the
                engagement GA4 cannot see — it is a comparison, not a correction to either figure.
              </Callout>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Seconds as m:ss above a minute, plain seconds below. A dash means NOT MEASURED. */
function duration(seconds: number | null): string {
  if (seconds === null || Number.isNaN(seconds)) return '—';
  if (seconds < 60) return `${seconds < 10 ? seconds.toFixed(1) : Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

function count(value: number | null): string {
  return value === null ? '—' : formatCompact(value);
}

function ratio(row: PageEngagementRow): string {
  if (row.ratio === null) return '—';
  return `${row.ratio.toFixed(2)}×`;
}

/**
 * The merged aliases worth naming.
 *
 * Variants that contributed ZERO views are dropped: GA4 reports some absolute URLs alongside real
 * paths, and they fold in correctly but carry no measurement. Listing them as "incl. https://… 0%"
 * would bury the one line that matters — a real alias actually splitting the traffic.
 */
function aliasNote(row: PageEngagementRow): string {
  return row.variants
    .filter((v) => v.path !== row.page && v.value > 0)
    .map((v) => `${v.path} ${Math.round(v.share * 100)}%`)
    .join(', ');
}
